import type { FieldValidator } from '@lucidtech/las-sdk-browser';
import { memo } from 'react';

import { LogoProps } from '@/components/core';
import {
  ConfidenceLogo,
  CustomCodeLogo,
  DatabaseMatchLogo,
  EmailLogo,
  IBANNumberLogo,
  LogicalExpressionLogo,
  NonEmptyLogo,
  NumericLogo,
  PhoneNumberLogo,
  RegexLogo,
  StringLogo,
  VariantLogoProps,
} from '@/components/logos';
import {
  VALIDATOR_BIC_SWIFT_FUNCTION_ID,
  VALIDATOR_CANNOT_BE_EMPTY_FUNCTION_ID,
  VALIDATOR_CONFIDENCE_FUNCTION_ID,
  VALIDATOR_CUSTOM_CODE_FUNCTION_ID,
  VALIDATOR_DATABASE_MATCH_FUNCTION_ID,
  VALIDATOR_EMAIL_FUNCTION_ID,
  VALIDATOR_FREIGHT_CONTAINER_ID_FUNCTION_ID,
  VALIDATOR_IBAN_FUNCTION_ID,
  VALIDATOR_LICENSE_PLATE_FUNCTION_ID,
  VALIDATOR_LOGEX_FUNCTION_ID,
  VALIDATOR_PASSPORT_NUMBER_FUNCTION_ID,
  VALIDATOR_PHONE_NUMBER_FUNCTION_ID,
  VALIDATOR_REGEX_FUNCTION_ID,
  VALIDATOR_TAX_ID_FUNCTION_ID,
  VALIDATOR_URL_FUNCTION_ID,
  VALIDATOR_VAT_ID_FUNCTION_ID,
} from '@/constants';

export type ValidatorLogoProps = LogoProps & {
  validator: Omit<FieldValidator, 'id'>;
};

const IValidatorLogo = ({ validator, size, ...rest }: ValidatorLogoProps) => {
  const logoProps: VariantLogoProps = { size, variant: 'validator' };

  switch (validator.functionId) {
    case VALIDATOR_CONFIDENCE_FUNCTION_ID:
      return <ConfidenceLogo {...logoProps} {...rest} />;
    case VALIDATOR_LOGEX_FUNCTION_ID:
      return <LogicalExpressionLogo {...logoProps} {...rest} />;
    case VALIDATOR_REGEX_FUNCTION_ID:
      return <RegexLogo {...logoProps} {...rest} />;
    case VALIDATOR_CUSTOM_CODE_FUNCTION_ID:
      return <CustomCodeLogo {...logoProps} {...rest} />;
    case VALIDATOR_CANNOT_BE_EMPTY_FUNCTION_ID:
      return <NonEmptyLogo {...logoProps} {...rest} />;
    case VALIDATOR_DATABASE_MATCH_FUNCTION_ID:
      return <DatabaseMatchLogo {...logoProps} {...rest} />;
    case VALIDATOR_IBAN_FUNCTION_ID:
      return <IBANNumberLogo {...logoProps} {...rest} />;
    case VALIDATOR_EMAIL_FUNCTION_ID:
      return <EmailLogo {...logoProps} {...rest} />;
    case VALIDATOR_PHONE_NUMBER_FUNCTION_ID:
      return <PhoneNumberLogo {...logoProps} {...rest} />;
    case VALIDATOR_URL_FUNCTION_ID:
      return <StringLogo {...logoProps} {...rest} />;
    case VALIDATOR_VAT_ID_FUNCTION_ID:
      return <NumericLogo {...logoProps} {...rest} />;
    case VALIDATOR_TAX_ID_FUNCTION_ID:
      return <NumericLogo {...logoProps} {...rest} />;
    case VALIDATOR_FREIGHT_CONTAINER_ID_FUNCTION_ID:
      return <NumericLogo {...logoProps} {...rest} />;
    case VALIDATOR_LICENSE_PLATE_FUNCTION_ID:
      return <NumericLogo {...logoProps} {...rest} />;
    case VALIDATOR_PASSPORT_NUMBER_FUNCTION_ID:
      return <NumericLogo {...logoProps} {...rest} />;
    case VALIDATOR_BIC_SWIFT_FUNCTION_ID:
      return <IBANNumberLogo {...logoProps} {...rest} />;
    default:
      return <StringLogo {...logoProps} {...rest} />;
  }
};

export const ValidatorLogo = memo(IValidatorLogo) as typeof IValidatorLogo;
